import { Link } from 'react-router-dom';
import { ArrowRight, Check, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { ForecasterLogo } from '../components/ForecasterLogo';
import { FooterSection } from '../components/sections';
import { useState } from 'react';

const tiers = [
    {
        name: 'Trial',
        price: 'Free',
        period: '14 days',
        description: 'Full platform access. No credit card required.',
        highlight: false,
        features: [
            'All optimization features enabled',
            'Up to 8 GPUs',
            'Self-hosted in your cluster',
            'Email setup support',
            'Full observability dashboard',
            'Clean uninstall anytime',
        ],
        cta: 'Start Free Trial',
        ctaLink: '/trial',
    },
    {
        name: 'Growth',
        price: '6% of GPU spend',
        period: 'pay as you save',
        description: 'Production use. Flat 6% of managed GPU spend. 7-10x ROI guaranteed.',
        highlight: true,
        features: [
            'All optimization features',
            'Unlimited GPUs',
            'Predictive autoscaling',
            'Multi-cloud cost arbitrage',
            'Policy engine',
            'Email support',
            'Flat 6% — simple, predictable',
        ],
        cta: 'Start Free Trial',
        ctaLink: '/trial',
    },
    {
        name: 'Enterprise',
        price: 'Custom',
        period: 'volume discounts',
        description: 'Dedicated support, SLAs, and custom integrations for large-scale deployments.',
        highlight: false,
        features: [
            'Everything in Growth',
            'Dedicated support engineer',
            'Custom SLA guarantees',
            'Custom integrations',
            'Volume discounts',
            'Priority feature requests',
            'Architecture review sessions',
        ],
        cta: 'Contact Us',
        ctaLink: '/trial',
    },
];

const pricingTiers = [
    { spend: 'Any GPU spend', rate: '6%' },
];

const faqs = [
    {
        question: 'How is GPU spend calculated?',
        answer: 'GPU spend is calculated based on the total cost of the GPU instances managed by Forecaster in your cluster. This includes on-demand, reserved, and spot instance costs across all providers. We use the same pricing data visible in your cloud billing dashboards.',
    },
    {
        question: 'What happens after the trial?',
        answer: 'After 14 days, Forecaster stops optimizing but your workloads continue running unchanged. There is no disruption. If you choose to continue, we activate billing based on your GPU spend tier. If not, a single Helm uninstall removes everything cleanly.',
    },
    {
        question: 'Can I cancel anytime?',
        answer: 'Yes. Forecaster is self-hosted with no lock-in. Run helm uninstall forecaster and it is completely removed. Your workloads, configurations, and Kubernetes resources are untouched. There are no cancellation fees or contractual minimums on the Growth plan.',
    },
    {
        question: 'Is my data safe?',
        answer: 'Forecaster runs entirely inside your Kubernetes cluster. No telemetry, workload data, or GPU metrics ever leave your environment. The controller operates with least-privilege RBAC permissions and uses read-only filesystem, non-root containers, and network policies by default.',
    },
];

const FAQItem = ({ question, answer }: { question: string; answer: string }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="border border-white/10 rounded-xl overflow-hidden">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-white/[0.02] transition-colors"
            >
                <span className="text-white font-medium pr-4">{question}</span>
                {isOpen ? (
                    <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                )}
            </button>
            {isOpen && (
                <div className="px-6 pb-6 text-gray-400 text-sm leading-relaxed">
                    {answer}
                </div>
            )}
        </div>
    );
};

export const Pricing = () => {
    return (
        <div className="min-h-screen bg-obsidian-900 text-white antialiased">
            {/* Navbar */}
            <nav className="fixed top-0 inset-x-0 z-50 bg-obsidian-900/80 backdrop-blur-xl border-b border-white/[0.06]">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <ForecasterLogo />
                    <Link
                        to="/trial"
                        className="px-5 py-2.5 bg-volt-500 text-obsidian-900 rounded-lg text-sm font-semibold hover:bg-volt-400 transition-all hover:shadow-volt"
                    >
                        Start Free Trial
                    </Link>
                </div>
            </nav>

            {/* Hero */}
            <section className="pt-32 pb-16 relative overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-volt-500/5 rounded-full blur-[150px]" />
                <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
                    <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                        Pay a fraction of what you save
                    </h1>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-4">
                        Flat 6% of managed GPU spend. 7-10x ROI.
                    </p>
                    <p className="text-gray-500">
                        Self-hosted in your cluster. No data leaves your environment.
                    </p>
                </div>
            </section>

            {/* Tier Cards */}
            <section className="pb-20">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid md:grid-cols-3 gap-6">
                        {tiers.map((tier) => (
                            <div
                                key={tier.name}
                                className={`relative rounded-2xl p-8 flex flex-col ${
                                    tier.highlight
                                        ? 'bg-gradient-to-br from-volt-500/10 via-volt-500/5 to-transparent border-2 border-volt-500/30'
                                        : 'bg-white/[0.02] border border-white/10'
                                }`}
                            >
                                {tier.highlight && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-volt-500 text-obsidian-900 rounded-full text-xs font-bold uppercase tracking-wide">
                                        Most Popular
                                    </div>
                                )}

                                <div className="mb-6">
                                    <h3 className="text-lg font-bold text-white mb-2">{tier.name}</h3>
                                    <div className="flex items-baseline gap-2 mb-1">
                                        <span className="text-3xl font-bold text-white">{tier.price}</span>
                                    </div>
                                    <p className="text-sm text-gray-500">{tier.period}</p>
                                </div>

                                <p className="text-sm text-gray-400 mb-6">{tier.description}</p>

                                <ul className="space-y-3 mb-8 flex-1">
                                    {tier.features.map((feature) => (
                                        <li key={feature} className="flex items-start gap-3">
                                            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${tier.highlight ? 'text-volt-400' : 'text-gray-500'}`} />
                                            <span className="text-sm text-gray-300">{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <Link
                                    to={tier.ctaLink}
                                    className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all group ${
                                        tier.highlight
                                            ? 'bg-volt-500 text-obsidian-900 hover:bg-volt-400'
                                            : 'bg-white/10 text-white hover:bg-white/20'
                                    }`}
                                >
                                    {tier.cta}
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Spend Tiers */}
            <section className="pb-20">
                <div className="max-w-3xl mx-auto px-6">
                    <div className="text-center mb-10">
                        <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                            Simple, flat pricing
                        </h2>
                        <p className="text-gray-400">
                            6% of managed GPU spend. No tiers, no surprises. Enterprise volume discounts available.
                        </p>
                    </div>

                    <div className="rounded-xl border border-white/10 overflow-hidden">
                        <div className="grid grid-cols-2 bg-white/[0.04] border-b border-white/10 px-6 py-3">
                            <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide">Monthly GPU Spend</span>
                            <span className="text-sm font-semibold text-gray-400 uppercase tracking-wide text-right">Forecaster Rate</span>
                        </div>
                        {pricingTiers.map((tier, i) => (
                            <div
                                key={tier.spend}
                                className={`grid grid-cols-2 px-6 py-4 ${
                                    i < pricingTiers.length - 1 ? 'border-b border-white/5' : ''
                                }`}
                            >
                                <span className="text-gray-300">{tier.spend}</span>
                                <span className="text-volt-400 font-semibold text-right">{tier.rate}</span>
                            </div>
                        ))}
                    </div>

                    <p className="text-center text-sm text-gray-500 mt-4">
                        Enterprise volume discounts available for $1M+ monthly GPU spend.
                    </p>
                </div>
            </section>

            {/* FAQ */}
            <section className="pb-24">
                <div className="max-w-3xl mx-auto px-6">
                    <div className="text-center mb-10">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-400 mb-6 text-sm font-medium">
                            <HelpCircle className="w-4 h-4" />
                            <span>FAQ</span>
                        </div>
                        <h2 className="text-2xl lg:text-3xl font-bold text-white">
                            Frequently asked questions
                        </h2>
                    </div>

                    <div className="space-y-3">
                        {faqs.map((faq) => (
                            <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="pb-24">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <div className="p-10 rounded-2xl bg-gradient-to-br from-volt-500/10 via-volt-500/5 to-transparent border border-volt-500/20">
                        <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                            See your savings in 14 days
                        </h2>
                        <p className="text-gray-400 mb-8 max-w-lg mx-auto">
                            Full access. No credit card. Runs in your cluster. If Forecaster does not save you money, uninstall with one command.
                        </p>
                        <Link
                            to="/trial"
                            className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-volt-500 text-obsidian-900 rounded-lg font-semibold text-lg hover:bg-volt-400 transition-colors group"
                        >
                            Start 14-Day Free Trial
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <p className="text-sm text-gray-500 mt-4">
                            Self-hosted -- No credit card -- 5-min setup
                        </p>
                    </div>
                </div>
            </section>

            <FooterSection />
        </div>
    );
};
